import { useSearch } from "@tanstack/react-router";
import { useGetRolesQuery } from "api/TenantSlug/Role/hooks";
import { useExportUsersMutation, useGetUsersQuery } from "api/TenantSlug/User/hooks.ts";
import { AddAffiliateDrawer } from "components/Drawers/AddAffiliateDrawer/AddAffiliateDrawer";
import { AffiliateDetailDrawer } from "components/Drawers/AffiliateDetailDrawer/AffiliateDetailDrawer.tsx";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom.ts";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { CommissionGroupCell } from "components/UI/Table/UI/CommissionGroupCell/CommissionGroupCell.tsx";
import { LabelsCell } from "components/UI/Table/UI/LabelsCell/LabelsCell.tsx";
import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell";
import { useDrawer } from "hooks/useDrawer";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { useRecoilValue } from "recoil";
import { PermissionEnum, RoleEnum } from "types/enums";
import { DrawerId, TableId } from "types/models.ts";

import UserData = App.Data.UserData;
import GetUsersResponse = App.Http.Responses.User.GetUsersResponse;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;

interface SearchParams {
    [`affiliateDetail-affiliateId`]: string;
}

export const AffiliatesTable = () => {
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const permissionUtils = usePermission();
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const tableId = TableId.AffiliatesTable;
    const tableAtomInstance = tableAtom<UserData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const drawerUtils = useDrawer();
    const getRolesQuery = useGetRolesQuery({});
    const affiliateRole = getRolesQuery.data?.roles.find((role) => role.name === RoleEnum.AffiliatePartner);
    const exportState = useRecoilValue(exportAtom(tableId));
    const canViewLabels = permissionUtils.hasPermission(PermissionEnum.UserLabelsView);
    const canViewForeignBalance = permissionUtils.hasPermission(PermissionEnum.BalanceViewForeign);
    const canEditCommissionGroup = permissionUtils.hasPermission(PermissionEnum.TenantUserSetCommissionGroups);
    const canCreateAffiliate = permissionUtils.hasPermission(PermissionEnum.UserCreate);

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                ...(tableState.filter?.filter ?? {}),
                ...{
                    "roles.id": affiliateRole?.id ?? "",
                },
            },
            sort: tableState.filter?.sort ?? "nickname",
            include: [canViewLabels && "labels", canViewForeignBalance && "settled_balance"].filter(Boolean).join(","),
        },
    };

    const query = useGetUsersQuery(request, {
        enabled: !!affiliateRole?.id,
    });
    const exportUsersMutation = useExportUsersMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetUsersResponse,
        void,
        Record<string, never>,
        BaseExportRequest
    >(tableId, query, undefined, undefined, exportUsersMutation, {
        additionalDependencies: [affiliateRole?.id],
        shouldFetch: !!affiliateRole?.id,
    });

    const columnConfig: TableColumnType<UserData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "nickname",
            key: "nickname",
            render: (record) => {
                return record;
            },
            sorter: true,
            defaultSorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            width: 150,
        },
        ...(canViewLabels
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("tags")} />,
                      dataIndex: "labels.name",
                      key: "labels.name",
                      render: (_: unknown, data: UserData) => <LabelsCell labels={data.labels} />,
                      filterable: true,
                      filterType: FILTER_TYPES.labelMultiSelect,
                      ellipsis: false,
                      width: 150,
                  },
              ]
            : []),
        ...(canViewForeignBalance
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("settled")} />,
                      dataIndex: "settled_balance",
                      key: "settled_balance",
                      render: (money: App.Data.MoneyData) => <MoneyCell money={money} />,
                      sorter: true,
                      filterable: true,
                      filterType: FILTER_TYPES.number,
                      ellipsis: true,
                      width: 120,
                  },
              ]
            : []),
        ...(canEditCommissionGroup
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("commission")} />,
                      dataIndex: "id",
                      key: "commissionGroup",
                      render: (userId: string) => <CommissionGroupCell userId={userId} />,
                      ellipsis: false,
                      width: 180,
                  },
              ]
            : []),
        /*{
            title: <ColumnHeaderTitle columnTitle="Země" />,
            dataIndex: "zeme",
            key: "zeme",
            render: () => {
                return (
                    <>
                        <Flag code={"CZ"} className={"h-[13px] rounded-[3px]"} />
                    </>
                );
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Jazyk" />,
            dataIndex: "jazyk",
            key: "jazyk",
            render: () => {
                return (
                    <>
                        <Flag code={"CZ"} className={"h-[13px] rounded-[3px]"} />
                    </>
                );
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Skupina" />,
            dataIndex: "user_commissionGroups",
            key: "user_commissionGroups",
            render: () => {
                const groupOptions = [
                    { label: "Základní", value: "default", key: "default" },
                    ...(fetchCommissionListQuery.data?.commissionGroups?.map((group) => ({
                        label: group.title,
                        value: group.id,
                        key: group.id,
                    })) || []),
                ];

                return <Select options={groupOptions} className="w-full" defaultValue={"default"} />;
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Kategorie" />,
            dataIndex: "kategorie",
            key: "kategorie",
            render: () => {
                const groupOptions = [{ label: "Základní", value: "default", key: "default" }];

                return <Select options={groupOptions} className="w-full" />;
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Štítky" />,
            key: "stitky",
            dataIndex: "stitky",
            render: () => (
                <Flex gap={"gap-x-2"} align="center">
                    <Tag closable closeIcon={<CloseOutlined />}>
                        TOP
                    </Tag>
                    <Tag closable closeIcon={<CloseOutlined />}>
                        Česko
                    </Tag>
                </Flex>
            ),
        },
        {
            title: <ColumnHeaderTitle columnTitle="Manažer" />,
            dataIndex: "manazer",
            key: "manazer",
            render: () => {
                const groupOptions = [{ label: "Základní", value: "default", key: "default" }];

                return <Select options={groupOptions} className="w-full" />;
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Stav" />,
            key: "stav",
            dataIndex: "stav",
            render: () => {
                const color = "green";
                return (
                    <Space>
                        <span style={{ color: color, fontSize: "24px" }}>•</span>
                        Povoleno
                    </Space>
                );
            },
        },
        {
            title: <ColumnHeaderTitle columnTitle="Schválení" />,
            key: "schvaleni",
            dataIndex: "schvaleni",
            render: (_, _data, index) => (
                <AcceptRejectTableButtons
                    isAccepted={index % 2 === 0}
                    isRejected={index % 2 !== 0}
                    onReject={() => console.log("rejected")}
                    onAccept={() => console.log("accepted")}
                />
            ),
        },*/
    ];

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<UserData>({
        entityName: "affiliate",
        detailPermission: PermissionEnum.AffiliateDetailView,
        detailDrawerId: DrawerId.AffiliateDetail,
        detailDrawerParam: `${DrawerId.AffiliateDetail}-affiliateId`,
    });

    return (
        <>
            {searchParams[`${DrawerId.AffiliateDetail}-affiliateId`] && (
                <AffiliateDetailDrawer affiliateId={searchParams[`${DrawerId.AffiliateDetail}-affiliateId`]} />
            )}
            {canCreateAffiliate && (
                <AddAffiliateDrawer
                    onSuccess={() => {
                        drawerUtils.hide(DrawerId.AddAffiliate);
                        tableFetchDataUtils.refetchData();
                    }}
                />
            )}
            <Table<GetUsersResponse, UserData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetUsersResponse}
                dataSource={tableFetchDataUtils.tableData?.users || []}
                columnConfig={columnConfig}
                loading={query.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                rowKey={(record) => record.id}
                addEntityData={{
                    drawerId: DrawerId.AddAffiliate,
                    permission: PermissionEnum.UserCreate,
                }}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                hideActionsColumn={true}
                enableFilters={true}
                searchDataIndex="nickname"
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                autoScrollWidth={true}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        extension: exportState.extension,
                        headers: exportState.headers,
                        export_all: exportState.range === "all",
                        page: request.page,
                        filter: request.filter,
                        delimiter: exportState.delimiter,
                    })
                }
            />
        </>
    );
};
